import { ReactNode, useCallback, useState } from "react";
import { ErrorProcessingContext } from "./ErrorProcessingContext";
import type { ErrorProcessor } from "@/types/ErrorProcessor";
import type { ServerErrors } from "@/types/ServerError";

export type ErrorProcessingProviderProps = {
  children: ReactNode;
};

export const ErrorProcessingProvider = ({
  children,
}: ErrorProcessingProviderProps) => {
  const [processors, setProcessors] = useState<Map<string, ErrorProcessor>>(
    new Map(),
  );

  const registerProcessor = useCallback(
    (processor: ErrorProcessor) => {
      setProcessors((prev) => {
        const newMap = new Map(prev);
        newMap.set(processor.name, processor);
        return newMap;
      });
    },
    [setProcessors],
  );

  const unregisterProcessor = useCallback(
    (processor: ErrorProcessor) => {
      setProcessors((prev) => {
        const newMap = new Map(prev);
        newMap.delete(processor.name);
        return newMap;
      });
    },
    [setProcessors],
  );

  const processErrors = useCallback(
    (serverErrors: ServerErrors) => {
      for (const processor of processors.values()) {
        if (processor.test(serverErrors)) {
          processor.execute(serverErrors);

          return {
            captured: true,
            processorName: processor.name,
          };
        }
      }

      return { captured: false };
    },
    [processors],
  );

  return (
    <ErrorProcessingContext
      value={{
        registerProcessor,
        unregisterProcessor,
        processErrors,
      }}
    >
      {children}
    </ErrorProcessingContext>
  );
};
